import { EventMeta } from '../../../interfaces/meta.interface';
import { riskyWords } from '../../utils/riskWords';

type MailItemsAccessedFields = {
    riskyKeyword: boolean;
};

function fieldsExtractor(log: any) {
    return {
        loginType: log.InternalLogonType,
        internalLoginType: log.LogonType,
        result: log.OperationResult,
        externalAccess: log.ExternalAccess,
        owner: log.MailboxOwnerUPN,
        client: log.ClientInfoString,
        riskyKeyword:
            !!log.AffectedItems && !!log.AffectedItems[0]['Subject']
                ? riskyWords.some((word) => log.AffectedItems[0]['Subject'].toLowerCase().includes(word.toLowerCase()))
                : false,
    };
}

const mailboxLogin: EventMeta<MailItemsAccessedFields> = {
    title: 'Mailbox Login',
    default: {
        description: 'Mailbox Login',
        longDescription: 'User signed into the mailbox.',
        severity: 'info',
    },
    fieldsExtractor,
    classifications: [
        {
            severity: 'critical',
            description: (fields) => `A suspicious IP attempted mailbox login`,
            longDescription: (fields) =>
                `A mailbox login was detected from a suspicious IP address that is not in your allowed list. This activity may indicate an attempt to access mailbox contents without authorization.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'danger',
            description: (fields) => `An unapproved IP attempted mailbox login`,
            longDescription: (fields) =>
                `A mailbox login was detected from an IP address that is not in your allowed list. This activity may indicate an attempt to access mailbox contents without authorization.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP signed into a mailbox`,
            longDescription: (fields) =>
                `A user successfully signed into their mailbox. The action originated from a suspicious IP. Mailbox logins are routine, but when they come from a suspicious IP, they may indicate an attempt to gain unauthorized access. Review the login details (time, source, and account activity) to confirm whether the access was legitimate or potentially malicious.`,
            predicates: {
                isFraudulentIP: true,
            },
        },
        {
            severity: 'info',
            description: (fields) => `A mailbox login was recorded`,
            longDescription: (fields) =>
                `A user signed into their mailbox. This is an informational log of a successful authentication event. No unusual conditions were detected. It does not require immediate action unless the login was unexpected or aligns with other anomalous activity.`,
            predicates: {
                isMicrosoftActivity: true,
            },
        },
        {
            severity: 'info',
            description: (fields) => `A mailbox login was recorded`,
            longDescription: (fields) =>
                `A user signed into their mailbox. This is an informational record of a successful authentication event. No unusual conditions were associated with this entry. No action is required unless the login was unexpected or aligns with other anomalies in mailbox activity.`,
        },
    ],
};

export default mailboxLogin;
